import React, { useEffect, useRef, useState, type FC } from "react";
import clsx from "clsx";
import { StatusDot } from "@/components/ui/status-dot";
import { Spinner } from "@/components/ui/spinner";
import type { TaskStep } from "@/services/api/data-hub/types";
import { TaskStepList } from "@/components/ui/task-steps-progress/TaskStepList";
import { TaskProgressBar } from "@/components/ui/task-steps-progress/TaskProgressBar";
import { TASK_CANCELLED_PROGRESS_FILL } from "@/components/ui/task-steps-progress/taskProgressTokens";
import { useResumeBannerLatch } from "@/hooks/useResumeBannerLatch";

export { groupTaskSteps } from "@/components/ui/task-steps-progress/groupTaskSteps";

interface TaskStepsProgressProps {
  title?: string;
  steps: TaskStep[];
  overallProgress: number;
  isComplete: boolean;
  hasFailed: boolean;
  isCancelled?: boolean;
  statusMessage?: string;
  /** Hide the step list until the first task payload arrives (or steps are defined). */
  isLoadingInitial?: boolean;
  /** Hide the uppercase title row (e.g. when step labels already show the active stage). */
  showTitle?: boolean;
  showStop?: boolean;
  showResume?: boolean;
  onStop?: () => void;
  onResume?: () => void;
  stopPending?: boolean;
  resumePending?: boolean;
  /** Fill the parent flex column; item lists scroll inside expanded groups only. */
  fillContainer?: boolean;
  /** Fired when any expandable item group is opened or all are collapsed. */
  onAnyItemGroupExpandedChange?: (expanded: boolean) => void;
}

const TaskStepsProgress: FC<TaskStepsProgressProps> = ({
  title = "Processing",
  steps,
  overallProgress,
  isComplete,
  hasFailed,
  isCancelled,
  statusMessage,
  isLoadingInitial = false,
  showTitle = true,
  showStop = false,
  showResume = false,
  onStop,
  onResume,
  stopPending = false,
  resumePending = false,
  fillContainer = false,
  onAnyItemGroupExpandedChange,
}) => {
  const percentage = Math.min(100, Math.max(0, overallProgress));
  const isRunning = !isComplete && !hasFailed && !isCancelled;
  const runningRef = useRef<HTMLLIElement>(null);
  const resumeBanner = useResumeBannerLatch(statusMessage, isRunning);
  const hasSteps = steps.length > 0;
  const showLoading = isLoadingInitial || (isRunning && !hasSteps && !resumeBanner.show);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (showLoading) {
      setRevealed(false);
      return;
    }
    const frame = requestAnimationFrame(() => setRevealed(true));
    return () => cancelAnimationFrame(frame);
  }, [showLoading]);

  useEffect(() => {
    if (resumeBanner.show || showLoading) return;
    runningRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [steps, resumeBanner.show, showLoading]);

  if (showLoading) {
    return (
      <div
        data-testid="task-steps-progress-loading"
        className="rounded-lg border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-800/30 p-6 flex flex-col items-center justify-center gap-3 min-h-[8rem]"
      >
        <Spinner size="lg" label={statusMessage || "Loading task progress..."} />
      </div>
    );
  }

  return (
    <div
      data-testid="task-steps-progress"
      className={clsx(
        "rounded-lg border border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-800/30 p-4 transition-opacity duration-300 ease-out",
        fillContainer ? "flex min-h-0 flex-1 flex-col overflow-hidden gap-3" : "space-y-3",
        revealed ? "opacity-100" : "opacity-0",
      )}
    >
      {showTitle && (
        <h4 className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide">
          {title}
        </h4>
      )}

      {resumeBanner.show && (
        <div
          data-testid="task-resume-banner"
          className="flex items-center gap-2 rounded-md border border-amber-200 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          <StatusDot color="amber" pulse size="sm" />
          <span className="font-medium">Resuming after interruption</span>
          {resumeBanner.message && (
            <span className="ml-auto text-amber-600/80 dark:text-amber-400/80 truncate">{resumeBanner.message}</span>
          )}
        </div>
      )}

      {hasSteps ? (
        <TaskStepList
          steps={steps}
          runningRef={runningRef}
          highlightIncomplete={isCancelled}
          expandableListFillsSpace={fillContainer}
          onAnyItemGroupExpandedChange={onAnyItemGroupExpandedChange}
        />
      ) : isRunning && statusMessage ? (
        <p data-testid="task-fallback-status" className="text-sm text-slate-600 dark:text-slate-300">
          {statusMessage}
        </p>
      ) : null}

      <div className={clsx("pt-1", fillContainer && "shrink-0")}>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {isComplete ? "Complete" : isCancelled ? "Cancelled" : hasFailed ? "Failed" : `${percentage}%`}
          </span>
        </div>
        <TaskProgressBar
          progress={percentage}
          fillClassName={
            isComplete ? "bg-emerald-500" : isCancelled ? TASK_CANCELLED_PROGRESS_FILL : hasFailed ? "bg-red-500" : "bg-indigo-500"
          }
          showStop={showStop}
          showResume={showResume}
          onStop={onStop}
          onResume={onResume}
          stopPending={stopPending}
          resumePending={resumePending}
          testId="task-steps-progress-bar"
        />
      </div>
    </div>
  );
};

export default TaskStepsProgress;
